import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import {
  getExamContent,
  getAttemptAnswers,
  submitAttempt,
  submitAnswer,
  getTimeRemaining,
  sendHeartbeat,
} from "../../../services/StudentAttemptService";
import { getQuestionTypes } from "../../../services/QuestionTypeService";
import { Trans, useTranslation } from "react-i18next";
import { resolveUploadUrl } from "../../../utils/uploads";

const RESYNC_INTERVAL_MS = 15000;
// Fréquence de retentative pour les réponses / la soumission finale restées
// en attente à cause d'une coupure réseau.
const OFFLINE_RETRY_INTERVAL_MS = 5000;

// ---------------------------------------------------------------------
// Persistance locale (résilience aux coupures internet / fermetures
// accidentelles de l'onglet pendant un examen) : tout ce que l'étudiant
// sélectionne est immédiatement écrit dans localStorage, avant même la
// tentative d'envoi au serveur. Si la requête échoue faute de réseau, la
// question reste marquée "en attente" et une boucle de retentative la
// renvoie dès que la connexion revient - aucune réponse n'est perdue,
// même si le navigateur est fermé entre-temps.
// ---------------------------------------------------------------------
const localAnswersKey = (attemptId) => `qcm_offline_answers_${attemptId}`;
const localPendingKey = (attemptId) => `qcm_offline_pending_${attemptId}`;
const localPendingSubmitKey = (attemptId) => `qcm_offline_pending_submit_${attemptId}`;

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage indisponible (mode privé plein, quota dépassé...) : on
    // continue sans persistance locale plutôt que de casser l'examen.
  }
};

const loadLocalAnswers = (attemptId) => readJSON(localAnswersKey(attemptId), {});
const saveLocalAnswers = (attemptId, answersObj) => writeJSON(localAnswersKey(attemptId), answersObj);

const loadPendingIds = (attemptId) => readJSON(localPendingKey(attemptId), []);
const savePendingIds = (attemptId, ids) => writeJSON(localPendingKey(attemptId), ids);

const hasPendingSubmit = (attemptId) => localStorage.getItem(localPendingSubmitKey(attemptId)) === "1";
const setPendingSubmitFlag = (attemptId) => {
  try {
    localStorage.setItem(localPendingSubmitKey(attemptId), "1");
  } catch {
    // ignore
  }
};
const clearAttemptLocalStorage = (attemptId) => {
  try {
    localStorage.removeItem(localAnswersKey(attemptId));
    localStorage.removeItem(localPendingKey(attemptId));
    localStorage.removeItem(localPendingSubmitKey(attemptId));
  } catch {
    // ignore
  }
};

// Une requête axios qui a reçu une réponse du serveur (400, 409, 500...)
// correspond à une vraie erreur métier - inutile de retenter. L'absence de
// `response` (timeout, coupure internet, DNS, CORS...) signale un problème
// réseau, seul cas où on retente automatiquement.
const isNetworkError = (err) => !err?.response;

// Certains examens sont rédigés entièrement en arabe (vérifié sur un
// exemple réel de votre banque de questions) : on détecte l'arabe dans le
// texte pour basculer automatiquement l'affichage en RTL, question par
// question (un examen peut mélanger les deux langues).
const ARABIC_RANGE = /[\u0600-\u06FF\u0750-\u077F]/;
const textDir = (text) => (text && ARABIC_RANGE.test(text) ? "rtl" : "ltr");

const formatTime = (totalSeconds) => {
  if (totalSeconds <= 0) return "00:00";
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const TakeExam = () => {
  const { t } = useTranslation();
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Contrôle d'identité : "Faut-il afficher la photo de l'étudiant pendant
  // le QCM ? Oui." - affichée en permanence dans l'en-tête pendant la
  // passation, pour que le surveillant puisse comparer visuellement.
  const currentUser = useSelector((state) => state.auth.auth?.user);
  const currentUserName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") || currentUser?.email;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadIsNetworkIssue, setLoadIsNetworkIssue] = useState(false);

  const [exam, setExam] = useState(location.state?.exam || null);
  const [questionTypes, setQuestionTypes] = useState([]);

  // Composition de l'examen : [{ id, question_id, content, points, answers: [{id, content}] }]
  const [composition, setComposition] = useState([]);
  // Réponses sélectionnées : { [question_id]: string[] }
  const [answers, setAnswers] = useState({});
  // Questions dont la dernière sélection n'a pas encore été confirmée par
  // le serveur (coupure réseau, requête en cours...).
  const [pendingAnswerIds, setPendingAnswerIds] = useState(() => new Set());

  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Soumission finale confirmée par l'étudiant mais pas encore acceptée par
  // le serveur (coupure réseau au moment d'envoyer) : on bloque la saisie
  // et on retente automatiquement jusqu'à ce que ça passe.
  const [pendingFinalSubmit, setPendingFinalSubmit] = useState(false);

  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);

  // Mode plein écran obligatoire / blocage copier-coller ("Faut-il un mode
  // plein écran obligatoire, un blocage du copier-coller et des autres
  // applications ? Oui."). Le navigateur exige un vrai geste utilisateur
  // pour entrer en plein écran : on affiche donc un écran d'accueil bloquant
  // avec un bouton avant de révéler les questions, plutôt que de tenter un
  // requestFullscreen() automatique (qui serait refusé silencieusement).
  const [fullscreenReady, setFullscreenReady] = useState(false);
  const [fullscreenExitedWarning, setFullscreenExitedWarning] = useState(false);

  const isDocFullscreen = () =>
    typeof document !== "undefined" && !!(document.fullscreenElement || document.webkitFullscreenElement);

  const enterFullscreen = () => {
    const el = document.documentElement;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (request) {
      request.call(el).then(() => setFullscreenReady(true)).catch(() => setFullscreenReady(true));
    } else {
      // API indisponible (navigateur non compatible) : on ne bloque pas
      // l'étudiant, on le laisse simplement continuer sans plein écran forcé.
      setFullscreenReady(true);
    }
  };

  useEffect(() => {
    if (!exam?.require_fullscreen || result) return undefined;
    const onFullscreenChange = () => {
      if (isDocFullscreen()) {
        setFullscreenExitedWarning(false);
      } else if (fullscreenReady) {
        setFullscreenExitedWarning(true);
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam?.require_fullscreen, result, fullscreenReady]);

  useEffect(() => {
    if (!exam?.block_copy_paste || result) return undefined;
    const block = (e) => e.preventDefault();
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("paste", block);
    document.addEventListener("contextmenu", block);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("paste", block);
      document.removeEventListener("contextmenu", block);
    };
  }, [exam?.block_copy_paste, result]);

  // Quitte le mode plein écran une fois l'examen soumis, pour rendre la main
  // à l'étudiant normalement.
  useEffect(() => {
    if (result && isDocFullscreen() && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  }, [result]);

  // Évite les soumissions concurrentes (minuteur local + resync serveur qui
  // détecteraient l'expiration en même temps).
  const finalizingRef = useRef(false);

  // Copies "toujours à jour" de l'état, pour que la boucle de retentative
  // (setInterval) et les listeners online/offline n'utilisent jamais des
  // valeurs figées au moment de leur création.
  const answersRef = useRef({});
  const pendingRef = useRef(new Set());
  const attemptIdRef = useRef(attemptId);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { pendingRef.current = pendingAnswerIds; }, [pendingAnswerIds]);
  useEffect(() => { attemptIdRef.current = attemptId; }, [attemptId]);

  const markPending = useCallback((questionId) => {
    setPendingAnswerIds((prev) => {
      const next = new Set(prev);
      next.add(questionId);
      savePendingIds(attemptIdRef.current, Array.from(next));
      return next;
    });
  }, []);

  const unmarkPending = useCallback((questionId) => {
    setPendingAnswerIds((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      savePendingIds(attemptIdRef.current, Array.from(next));
      return next;
    });
  }, []);

  // Tente d'envoyer une réponse au serveur. En cas d'échec réseau, la
  // question reste marquée "en attente" pour être retentée automatiquement ;
  // en cas de rejet métier (tentative expirée/soumise...), on abandonne la
  // retentative pour cette question et on informe l'étudiant.
  const syncAnswer = useCallback(async (questionId, selectedIds) => {
    try {
      await submitAnswer(attemptIdRef.current, { question_id: questionId, selected_answer_ids: selectedIds });
      unmarkPending(questionId);
    } catch (err) {
      if (!isNetworkError(err)) {
        unmarkPending(questionId);
        setSubmitError(
          err?.response?.data?.message || t("pages.takeExam.answerSyncError"),
        );
      }
      // Erreur réseau : on ne fait rien de plus, la question reste
      // "pending" et sera renvoyée par la boucle de retentative.
    }
  }, [unmarkPending, t]);

  const handleFinalSubmit = useCallback(async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    setPendingSubmitFlag(attemptIdRef.current);
    try {
      const res = await submitAttempt(attemptIdRef.current);
      const body = res.data?.data;
      setResult(body?.result || null);
      setTimedOut(!!body?.timedOut);
      setPendingFinalSubmit(false);
      clearAttemptLocalStorage(attemptIdRef.current);
    } catch (err) {
      if (isNetworkError(err)) {
        // Pas de connexion au moment de soumettre : on garde la demande de
        // soumission active, la boucle de retentative la renverra dès que
        // possible. On NE relâche PAS `finalizingRef` tout de suite pour
        // éviter un double clic pendant l'attente, mais on autorise la
        // boucle automatique à réessayer.
        setPendingFinalSubmit(true);
        setSubmitError(
          t("pages.takeExam.offlineSubmitNotice"),
        );
      } else {
        setSubmitError(err?.response?.data?.message || t("pages.takeExam.submitError"));
        setPendingFinalSubmit(false);
      }
      finalizingRef.current = false;
    } finally {
      setSubmitting(false);
      setShowConfirmSubmit(false);
    }
  }, [t]);

  // Boucle de retentative "hors ligne" : toutes les OFFLINE_RETRY_INTERVAL_MS
  // et à chaque retour de connexion, on renvoie les réponses en attente puis,
  // s'il y en a une, la soumission finale en attente. C'est ce mécanisme qui
  // garantit qu'une coupure internet ne fait perdre ni réponse ni soumission.
  const flushPending = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;

    const pendingIds = Array.from(pendingRef.current);
    if (pendingIds.length) {
      await Promise.all(
        pendingIds.map((qid) => syncAnswer(qid, answersRef.current[qid] || [])),
      );
    }

    if (hasPendingSubmit(attemptIdRef.current) && !result) {
      finalizingRef.current = false;
      handleFinalSubmit();
    }
  }, [handleFinalSubmit, result, syncAnswer]);

  // Heartbeat "je suis toujours là" : si le serveur détecte un écart anormal
  // depuis le précédent appel (coupure de courant/connexion), il crédite
  // automatiquement ce temps perdu à la tentative - voir
  // services/studentAttempts.service.js#heartbeat côté backend.
  const sendHeartbeatSafe = useCallback(() => {
    sendHeartbeat(attemptIdRef.current).catch(() => {
      // Échec ponctuel (souvent réseau) : sans gravité, le prochain appel
      // (retour en ligne ou cycle suivant) créditera un écart plus large.
    });
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setIsOffline(false);
      flushPending();
      sendHeartbeatSafe();
    };
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushPending, sendHeartbeatSafe]);

  useEffect(() => {
    if (result) return undefined;
    const interval = setInterval(flushPending, OFFLINE_RETRY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [flushPending, result]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      setLoadIsNetworkIssue(false);
      try {
        // Endpoint unique : questions + options de réponse, is_correct
        // n'est jamais transmis au navigateur avant la soumission.
        const [contentRes, typesRes, existingAnswersRes] = await Promise.all([
          getExamContent(attemptId),
          getQuestionTypes({ page: 1, limit: 100 }),
          getAttemptAnswers(attemptId),
        ]);

        const content = contentRes.data?.data;

        if (content.attempt.status === "timed_out") {
          setTimedOut(true);
          setLoadError(t("pages.takeExam.timedOutLoadError"));
          clearAttemptLocalStorage(attemptId);
          setLoading(false);
          return;
        }
        if (content.attempt.status !== "in_progress") {
          setLoadError(t("pages.takeExam.notInProgressError"));
          clearAttemptLocalStorage(attemptId);
          setLoading(false);
          return;
        }

        setExam(content.exam);
        setComposition(content.questions || []);
        setQuestionTypes(typesRes.data?.data || []);

        // Reprise de tentative (rechargement de page, coupure internet...) :
        // on part des réponses confirmées côté serveur, puis on superpose
        // les réponses locales encore "en attente" (pas encore synchronisées
        // au moment où la page a été quittée) pour ne rien perdre.
        const existingAnswers = existingAnswersRes.data?.data || [];
        const serverAnswersMap = {};
        existingAnswers.forEach((a) => {
          serverAnswersMap[a.question_id] = a.selected_answer_ids || [];
        });

        const localSnapshot = loadLocalAnswers(attemptId);
        const localPendingIds = new Set(loadPendingIds(attemptId));
        const mergedAnswers = { ...serverAnswersMap };
        localPendingIds.forEach((qid) => {
          if (localSnapshot[qid] !== undefined) mergedAnswers[qid] = localSnapshot[qid];
        });

        setAnswers(mergedAnswers);
        saveLocalAnswers(attemptId, mergedAnswers);
        setPendingAnswerIds(localPendingIds);

        // Minuteur : la valeur envoyée par le serveur fait foi (calculée à
        // partir de son horloge, pas de celle du navigateur).
        setRemainingSeconds(content.remaining_seconds);

        // S'il restait des réponses ou une soumission en attente d'une
        // session précédente (onglet fermé pendant une coupure), on tente
        // de les renvoyer tout de suite.
        if (localPendingIds.size > 0 || hasPendingSubmit(attemptId)) {
          setTimeout(() => flushPending(), 0);
        }
      } catch (err) {
        if (isNetworkError(err)) {
          setLoadIsNetworkIssue(true);
          setLoadError(t("pages.takeExam.networkRetrying"));
        } else {
          setLoadError(err?.response?.data?.message || t("pages.takeExam.loadError"));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  // Si le chargement initial a échoué à cause du réseau (et non d'une
  // erreur métier), on retente automatiquement au lieu de laisser
  // l'étudiant bloqué sur un écran d'erreur.
  useEffect(() => {
    if (!loadIsNetworkIssue) return undefined;
    const retry = setInterval(() => {
      if (typeof navigator === "undefined" || navigator.onLine) {
        setLoading(true);
        getExamContent(attemptId)
          .then(() => window.location.reload())
          .catch(() => {});
      }
    }, OFFLINE_RETRY_INTERVAL_MS);
    return () => clearInterval(retry);
  }, [loadIsNetworkIssue, attemptId]);

  // Décompte local (affichage seconde par seconde) : purement visuel, la
  // valeur qui compte réellement est resynchronisée périodiquement ci-dessous.
  useEffect(() => {
    if (remainingSeconds === null || result) return undefined;
    if (remainingSeconds <= 0) {
      handleFinalSubmit();
      return undefined;
    }
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => (prev !== null ? Math.max(0, prev - 1) : prev));
    }, 1000);
    return () => clearInterval(timer);
  }, [remainingSeconds, result, handleFinalSubmit]);

  // Resynchronisation "temps réel" avec le serveur : toutes les 15s, on
  // interroge /time-remaining pour recaler le minuteur sur l'horloge serveur
  // et détecter une expiration (ou une annulation par un enseignant) même si
  // l'onglet a perdu le focus ou que le minuteur local a dérivé.
  useEffect(() => {
    if (result || loading || loadError) return undefined;

    const resync = async () => {
      setSyncing(true);
      try {
        const res = await getTimeRemaining(attemptId);
        const { status, remaining_seconds } = res.data?.data || {};
        if (status && status !== "in_progress") {
          // Le serveur a déjà finalisé la tentative (expirée ou annulée) :
          // on va chercher le résultat correspondant plutôt que de resoumettre.
          handleFinalSubmit();
          return;
        }
        setRemainingSeconds(remaining_seconds);
      } catch (err) {
        // Échec de resync ponctuel (souvent une coupure réseau) : on garde
        // le décompte local, la boucle de retentative hors-ligne et le
        // prochain cycle s'en chargeront.
      } finally {
        setSyncing(false);
      }
    };

    const interval = setInterval(resync, RESYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [attemptId, result, loading, loadError, handleFinalSubmit]);

  const typeById = (id) => questionTypes.find((t) => t.id === id);

  // Regroupe les questions par cas clinique (dossier progressif) : les
  // questions indépendantes restent des entrées isolées, les questions
  // liées à un même cas clinique sont rassemblées sous une seule vignette,
  // triées par leur ordre progressif au sein du cas. L'étudiant peut
  // toujours faire défiler librement et revenir sur une réponse précédente
  // (pas de verrouillage entre les questions d'un même cas).
  const groupedComposition = useMemo(() => {
    const groups = [];
    const caseIndex = new Map();
    composition.forEach((item) => {
      if (item.clinical_case) {
        const caseId = item.clinical_case.id;
        if (!caseIndex.has(caseId)) {
          caseIndex.set(caseId, groups.length);
          groups.push({ type: "case", clinicalCase: item.clinical_case, items: [] });
        }
        groups[caseIndex.get(caseId)].items.push(item);
      } else {
        groups.push({ type: "single", item });
      }
    });
    groups.forEach((g) => {
      if (g.type === "case") {
        g.items.sort((a, b) => (a.case_order_index ?? 0) - (b.case_order_index ?? 0));
      }
    });
    return groups;
  }, [composition]);

  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const handleSelectAnswer = (questionId, answerId, allowsMultiple) => {
    if (pendingFinalSubmit) return; // soumission déjà lancée : on verrouille la saisie

    const current = answers[questionId] || [];
    const updated = allowsMultiple
      ? current.includes(answerId)
        ? current.filter((id) => id !== answerId)
        : [...current, answerId]
      : [answerId];

    const nextAnswers = { ...answers, [questionId]: updated };
    setAnswers(nextAnswers);
    // Écriture locale IMMÉDIATE, avant même la tentative réseau : c'est ce
    // qui protège la réponse si la coupure survient pile à ce moment-là.
    saveLocalAnswers(attemptId, nextAnswers);
    markPending(questionId);

    syncAnswer(questionId, updated);
  };

  const answeredCount = useMemo(
    () => composition.filter((item) => (answers[item.question_id] || []).length > 0).length,
    [composition, answers],
  );

  const pendingCount = pendingAnswerIds.size;

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.crud.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (loadError && !result) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className={`alert ${loadIsNetworkIssue ? "alert-info" : "alert-warning"}`}>
            {loadError}
          </div>
          {!loadIsNetworkIssue && (
            <button type="button" className="btn btn-primary" onClick={() => navigate("/my-exams")}>
              {t("pages.takeExam.backToMyExams")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className={`ti ti-${result.passed ? "circle-check text-success" : "circle-x text-danger"} fs-1`}></i>
              <h3 className="mt-3">{timedOut ? t("pages.takeExam.timedOutTitle") : t("pages.takeExam.submittedTitle")}</h3>
              {exam?.show_results_immediately !== false ? (
                <>
                  <p className="fs-4 mb-1">
                    <Trans i18nKey="pages.takeExam.scoreSummary" values={{ percentage: result.score_percentage, earned: result.total_points_earned, possible: result.total_points_possible }} components={[<strong />]} />
                  </p>
                  <span className={`badge badge-soft-${result.passed ? "success" : "danger"} fs-6`}>
                    {result.passed ? t("common.ui.admis") : t("common.ui.nonAdmis")}
                  </span>
                </>
              ) : (
                <p className="text-muted">{t("pages.takeExam.resultsLater")}</p>
              )}
              <div className="mt-4">
                <button type="button" className="btn btn-primary" onClick={() => navigate("/my-exams")}>
                  {t("pages.takeExam.backToMyExams")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (exam?.require_fullscreen && !fullscreenReady) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-maximize fs-1 text-primary"></i>
              <h3 className="mt-3">{t("pages.takeExam.cetExamenDoitEtrePassePlein")}</h3>
              <p className="text-muted">
                {t("pages.takeExam.cliquezSurBoutonCiDessousPour")}
              </p>
              <button type="button" className="btn btn-primary btn-lg mt-2" onClick={enterFullscreen}>
                <i className="ti ti-maximize me-2"></i>{t("pages.takeExam.passerPleinEcranCommencer")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {(isOffline || pendingCount > 0 || pendingFinalSubmit) && (
          <div className="alert alert-warning d-flex align-items-center mb-3">
            <i className="ti ti-wifi-off me-2"></i>
            <div>
              {isOffline
                ? t("pages.takeExam.offlineLostConnection")
                : pendingCount > 0
                ? t("pages.takeExam.offlineUnstable")
                : ""}
              {pendingFinalSubmit
                ? t("pages.takeExam.offlinePendingSubmitNotice")
                : pendingCount > 0
                ? t("pages.takeExam.offlinePendingAnswersNotice", { count: pendingCount })
                : t("pages.takeExam.offlineLocalSaveNotice")}
            </div>
          </div>
        )}

        {fullscreenExitedWarning && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3">
            <div>
              <i className="ti ti-alert-triangle me-2"></i>
              {t("pages.takeExam.vousAvezQuitteModePleinEcran")}
            </div>
            <button type="button" className="btn btn-sm btn-danger" onClick={enterFullscreen}>
              {t("pages.takeExam.revenirPleinEcran")}
            </button>
          </div>
        )}

        <div
          className="d-flex align-items-center justify-content-between mb-3 p-3 rounded"
          style={{ background: "#fff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
        >
          <div className="d-flex align-items-center gap-3">
            <span
              className="avatar avatar-md flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center overflow-hidden"
              title={t("pages.takeExam.controleIdentite")}
            >
              {currentUser?.avatar_url ? (
                <img src={currentUser.avatar_url} className="img-fluid rounded-circle" alt={currentUserName} />
              ) : (
                <i className="ti ti-user fs-4"></i>
              )}
            </span>
            <div>
              <h4 className="mb-0">{exam?.title}</h4>
              <span className="text-muted small">
                {/* {currentUserName}
                {" · "} */}
                {t("pages.takeExam.questionsAnswered", { answered: answeredCount, total: composition.length })}
                {syncing && <span className="ms-2">{t("pages.takeExam.synchronisation")}</span>}
              </span>
            </div>
          </div>
          <div className="text-end">
            <span className={`fs-3 fw-bold ${remainingSeconds < 60 ? "text-danger" : "text-primary"}`}>
              <i className="ti ti-clock me-1"></i>
              {formatTime(remainingSeconds || 0)}
            </span>
          </div>
        </div>

        {submitError && <div className="alert alert-danger">{submitError}</div>}

        {(() => {
          const renderQuestionCard = (item, questionNumber) => {
            const type = typeById(item.type_id);
            const allowsMultiple = !!type?.allows_multiple_correct;
            const selected = answers[item.question_id] || [];
            const isPending = pendingAnswerIds.has(item.question_id);

            return (
              <div className="card mb-3" key={item.id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <h6 className="mb-0">
                      {t("pages.takeExam.questionCounter", { current: questionNumber, total: composition.length })}
                      {isPending && (
                        <span className="badge badge-soft-warning ms-2" title={t("pages.takeExam.pendingSyncHint")}>
                          {t("pages.takeExam.pendingSyncBadge")}
                        </span>
                      )}
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      {item.requires_calculator && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCalculatorOpen(true)}
                        >
                          <i className="ti ti-calculator me-1"></i>{t("pages.takeExam.calculatrice")}
                        </button>
                      )}
                      <span className="badge badge-soft-info">{t("pages.takeExam.pointsUnit", { points: item.points })}</span>
                    </div>
                  </div>
                  <p className="mb-3" dir={textDir(item.content)}>{item.content}</p>
                  {item.image_url && (
                    <div className="mb-3 text-center">
                      <img
                        src={resolveUploadUrl(item.image_url)}
                        alt={t("pages.takeExam.imageAlt")}
                        className="img-fluid rounded border"
                        style={{ maxHeight: "360px", objectFit: "contain" }}
                      />
                    </div>
                  )}

                  {item.reference_values?.length > 0 && (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("common.ui.parametre")}</th>
                            <th>{t("common.ui.valeursNormales")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.reference_values.map((rv) => (
                            <tr key={rv.id}>
                              <td>
                                {rv.category ? `${rv.category} — ` : ""}
                                {rv.name}
                              </td>
                              <td>
                                {rv.normal_range_text ||
                                  [
                                    rv.normal_range_min !== null && rv.normal_range_min !== undefined ? rv.normal_range_min : null,
                                    rv.normal_range_max !== null && rv.normal_range_max !== undefined ? rv.normal_range_max : null,
                                  ]
                                    .filter((v) => v !== null)
                                    .join(" – ") || "-"}
                                {rv.unit ? ` ${rv.unit}` : ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {item.answers?.map((option) => (
                    <div className="form-check mb-2" key={option.id}>
                      <input
                        className="form-check-input"
                        type={allowsMultiple ? "checkbox" : "radio"}
                        name={`question-${item.question_id}`}
                        id={`option-${option.id}`}
                        checked={selected.includes(option.id)}
                        disabled={pendingFinalSubmit}
                        onChange={() => handleSelectAnswer(item.question_id, option.id, allowsMultiple)}
                      />
                      <label className="form-check-label" htmlFor={`option-${option.id}`} dir={textDir(option.content)}>
                        {option.content}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
          };

          let counter = 0;
          return groupedComposition.map((group, gi) => {
            if (group.type === "single") {
              counter += 1;
              return renderQuestionCard(group.item, counter);
            }
            return (
              <div className="card mb-3 border-info" key={`case-${group.clinicalCase.id}-${gi}`}>
                <div className="card-header bg-light-subtle">
                  <h6 className="mb-1">
                    <i className="ti ti-stethoscope me-2"></i>{t("pages.takeExam.casClinique", { title: group.clinicalCase.title })}
                  </h6>
                  <p className="mb-0 text-muted" dir={textDir(group.clinicalCase.stem)}>{group.clinicalCase.stem}</p>
                  {group.clinicalCase.image_url && (
                    <img
                      src={resolveUploadUrl(group.clinicalCase.image_url)}
                      alt=""
                      className="img-fluid mt-2 rounded"
                      style={{ maxHeight: "240px" }}
                    />
                  )}
                </div>
                <div className="card-body bg-light pt-3">
                  {group.items.map((item) => {
                    counter += 1;
                    return renderQuestionCard(item, counter);
                  })}
                </div>
              </div>
            );
          });
        })()}

        <div className="d-flex justify-content-end mb-4">
          <button
            type="button"
            className="btn btn-success btn-lg"
            onClick={() => setShowConfirmSubmit(true)}
            disabled={pendingFinalSubmit}
          >
            <i className="ti ti-send me-2"></i>
            {pendingFinalSubmit ? t("pages.takeExam.submitPendingConnection") : t("pages.takeExam.submitExam")}
          </button>
        </div>
      </div>

      <Modal show={showConfirmSubmit} onHide={() => (submitting ? null : setShowConfirmSubmit(false))} centered>
        <div className="modal-body text-center">
          <h4>{t("pages.takeExam.confirmSubmitTitle")}</h4>
          <p>
            {t("pages.takeExam.confirmSubmitBody", { answered: answeredCount, total: composition.length })}
          </p>
          {pendingCount > 0 && (
            <p className="text-warning small">
              {t("pages.takeExam.pendingAnswersWarning", { count: pendingCount })}
            </p>
          )}
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={() => setShowConfirmSubmit(false)}
              disabled={submitting}
            >
              {t("pages.takeExam.continueExam")}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                finalizingRef.current = false;
                handleFinalSubmit();
              }}
              disabled={submitting}
            >
              {submitting ? t("pages.takeExam.submitting") : t("pages.takeExam.confirmSubmit")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal show={calculatorOpen} onHide={() => setCalculatorOpen(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.takeExam.calculatrice")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SimpleCalculator />
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Calculatrice basique (4 opérations) mise à disposition de l'étudiant pour
// les questions de dosage marquées "requires_calculator". Aucune donnée
// n'est envoyée au serveur : purement un outil de calcul côté client.
const SimpleCalculator = () => {
  const { t } = useTranslation();
  const [display, setDisplay] = useState("0");
  const [pending, setPending] = useState(null); // { value, operator }
  const [resetOnNextDigit, setResetOnNextDigit] = useState(false);

  const inputDigit = (digit) => {
    if (resetOnNextDigit || display === "0") {
      setDisplay(digit === "." ? "0." : digit);
      setResetOnNextDigit(false);
    } else if (digit === "." && display.includes(".")) {
      // ignore un second point décimal
    } else {
      setDisplay(display + digit);
    }
  };

  const compute = (a, b, operator) => {
    switch (operator) {
      case "+": return a + b;
      case "-": return a - b;
      case "×": return a * b;
      case "÷": return b === 0 ? NaN : a / b;
      default: return b;
    }
  };

  const applyOperator = (operator) => {
    const value = parseFloat(display);
    if (pending) {
      const result = compute(pending.value, value, pending.operator);
      setDisplay(String(Number.isFinite(result) ? result : t("common.ui.erreur")));
      setPending(operator === "=" ? null : { value: result, operator });
    } else if (operator !== "=") {
      setPending({ value, operator });
    }
    setResetOnNextDigit(true);
  };

  const clearAll = () => {
    setDisplay("0");
    setPending(null);
    setResetOnNextDigit(false);
  };

  const buttons = [
    ["7", "8", "9", "÷"],
    ["4", "5", "6", "×"],
    ["1", "2", "3", "-"],
    ["0", ".", "=", "+"],
  ];

  return (
    <div>
      <div className="form-control text-end fs-4 mb-2" style={{ background: "#f5f5f5" }}>
        {display}
      </div>
      <div className="d-grid gap-2 mb-2">
        <button type="button" className="btn btn-outline-danger btn-sm" onClick={clearAll}>
          C
        </button>
      </div>
      {buttons.map((row, ri) => (
        <div className="d-flex gap-2 mb-2" key={ri}>
          {row.map((btn) => (
            <button
              key={btn}
              type="button"
              className={`btn flex-fill ${["÷", "×", "-", "+", "="].includes(btn) ? "btn-primary" : "btn-light"}`}
              onClick={() => (["÷", "×", "-", "+", "="].includes(btn) ? applyOperator(btn) : inputDigit(btn))}
            >
              {btn}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default TakeExam;
